import { useMemo, useState, useCallback } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'
import { useWardStore } from '@/stores/wardStore'
import { VI_MESSAGES } from '@/constants/vietnamese'
import { formatNumber } from '@/utils/vietnamese-format'
import { cn } from '@/lib/utils'
import { debounce } from '@/utils/debounce'
import { RangeSlider } from './RangeSlider'

export function WardFilters() {
  const { wards, filters, updateFilters, resetFilters } = useWardStore()
  const [isExpanded, setIsExpanded] = useState(false)

  // Tính toán giá trị min/max và danh sách quận
  const { districts, areaRange, populationRange } = useMemo(() => {
    if (wards.length === 0) {
      return {
        districts: [],
        areaRange: { min: 0, max: 100 },
        populationRange: { min: 0, max: 100000 },
      }
    }

    const uniqueDistricts = Array.from(new Set(wards.map((w) => w.xaphuong))).sort()
    const areas = wards.map((w) => w.dientichpl || 0)
    const populations = wards.map((w) => w.danso || 0)

    return {
      districts: uniqueDistricts,
      areaRange: {
        min: Math.min(...areas),
        max: Math.max(...areas),
      },
      populationRange: {
        min: Math.min(...populations),
        max: Math.max(...populations),
      },
    }
  }, [wards])

  // Đếm số bộ lọc đang hoạt động
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.selectedDistrict) count++
    if (filters.areaRange) count++
    if (filters.populationRange) count++
    return count
  }, [filters])

  const handleDistrictChange = useCallback(
    (district: string) => {
      updateFilters({
        selectedDistrict: district === '' ? null : district,
      })
    },
    [updateFilters]
  )

  // Debounced range updates
  const debouncedAreaUpdate = useCallback(
    debounce((values: [number, number]) => {
      updateFilters({
        areaRange: values,
      })
    }, 300),
    [updateFilters]
  )

  const debouncedPopulationUpdate = useCallback(
    debounce((values: [number, number]) => {
      updateFilters({
        populationRange: values,
      })
    }, 300),
    [updateFilters]
  )

  const handleAreaRangeChange = useCallback(
    (values: [number, number]) => {
      debouncedAreaUpdate(values)
    },
    [debouncedAreaUpdate]
  )

  const handlePopulationRangeChange = useCallback(
    (values: [number, number]) => {
      debouncedPopulationUpdate(values)
    },
    [debouncedPopulationUpdate]
  )

  const handleClearFilters = useCallback(() => {
    resetFilters()
  }, [resetFilters])

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  return (
    <div className="space-y-3">
      {/* Nút mở rộng bộ lọc */}
      <button
        onClick={toggleExpanded}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm',
          'transition-colors hover:bg-gray-100',
          isExpanded && 'bg-gray-100'
        )}
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          <span>{VI_MESSAGES.WARD.FILTERS.TITLE}</span>
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">
              {activeFilterCount}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">{isExpanded ? 'Thu gọn' : 'Mở rộng'}</span>
      </button>

      {/* Nội dung bộ lọc */}
      {isExpanded && (
        <div className="space-y-4 rounded-lg border bg-gray-50 p-4">
          {/* Bộ lọc theo quận */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {VI_MESSAGES.WARD.FILTERS.DISTRICT}
            </label>
            <select
              value={filters.selectedDistrict || ''}
              onChange={(e) => handleDistrictChange(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            >
              <option value="">Tất cả phường/xã</option>
              {districts.map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
          </div>

          {/* Bộ lọc theo diện tích */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {VI_MESSAGES.WARD.FILTERS.AREA_RANGE}
            </label>
            <RangeSlider
              min={areaRange.min}
              max={areaRange.max}
              values={filters.areaRange || [areaRange.min, areaRange.max]}
              onChange={handleAreaRangeChange}
              formatValue={(value) => `${formatNumber(value)} km²`}
            />
          </div>

          {/* Bộ lọc theo dân số */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {VI_MESSAGES.WARD.FILTERS.POPULATION_RANGE}
            </label>
            <RangeSlider
              min={populationRange.min}
              max={populationRange.max}
              values={filters.populationRange || [populationRange.min, populationRange.max]}
              onChange={handlePopulationRangeChange}
              formatValue={(value) => formatNumber(value)}
            />
          </div>

          {/* Nút xóa bộ lọc */}
          {activeFilterCount > 0 && (
            <button
              onClick={handleClearFilters}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
            >
              <X className="h-4 w-4" />
              {VI_MESSAGES.WARD.ACTIONS.CLEAR_FILTERS}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
